import { useProjectParams } from '@hooks/useProjectParams';
import { ConfirmationDialog } from 'components/ConfirmationDialog';
import { ErrorHandlingModal } from 'components/ErrorHandlingModal';
import { ControlledDateField, ControlledSelectField, ControlledTextField, ControlNumberField } from 'components/Form';
import { GroupHeader } from 'components/GroupHeader';
import { ModalDialog } from 'components/ModalDialog';
import { WorkOrderChangeStatusProps } from 'constantsProject';
import { RequestStatus } from 'features/request/requestSlice';
import type { BimWorkOrderChangeQuery, Maybe, ProjectData } from 'gql/graphql';
import { ContractStatusEnum, WorkOrderChangeStatusEnum, WorkOrderStatusEnum } from 'gql/graphql';
import { useSendGpnAnalyticsEvent } from 'hooks/useSendGpnAnalyticsEvent';
import type { AnyObject } from 'types/AnyObject';
import type { DefaultItemSelect } from 'uikit';
import { cnMixSpace, Grid, GridItem } from 'uikit';
import { requiredBaseRule, requiredContractChangeApprovedRule } from 'utils/constants';
import { ExportCSVReport } from 'utils/createCSVReportFile';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { mapDataToForm } from './helpers';
import {
    useCreateWorkOrderChange,
    useIsUniqueWorkOrderChangeInternalNumber,
    useUpdateWorkOrderChange,
    useWorkOrderChangeDefaults,
} from './hooks';
import type {
    FieldValuesBimWorkOrderChangeCreateOrEditStructure,
    TransformedValuesBimWorkOrderChangeCreateOrEditStructure,
} from './types';

import Styles from './BimWorkOrderChangeCreateOrEdit.module.css';

interface IBimWorkOrderChangeCreateOrEdit {
    contractStatus: ContractStatusEnum | undefined;
    isCreating?: boolean;
    isFrameContract: boolean;
    onClose: () => void;
    onSuccess?: () => void;
    projectData: ProjectData | null | undefined;
    refreshProjectData: () => void;
    refreshWorkOrderChangeData?: () => void;
    workOrderChangeData?: Maybe<BimWorkOrderChangeQuery['bimWorkOrderChange']['data'] | undefined>;
    workOrderId: number;
    statusWorkOrderChange?: WorkOrderChangeStatusEnum;
}

export const BimWorkOrderChangeCreateOrEdit = ({
    contractStatus,
    isCreating,
    isFrameContract,
    onClose,
    onSuccess,
    workOrderId,
    projectData,
    refreshProjectData,
    refreshWorkOrderChangeData,
    workOrderChangeData,
    statusWorkOrderChange,
}: IBimWorkOrderChangeCreateOrEdit) => {
    const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
    const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] = useState<boolean>(false);
    const [isConfirmationDialogChangeStatus, setIsConfirmationDialogChangeStatus] = useState<boolean>(false);
    const [listSelectStatusValue, setListSelectStatusValue] = useState<(DefaultItemSelect | undefined)[]>([]);

    const formRef = useRef<HTMLFormElement>(null);

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { id: projectId, orderChangeId, methodology } = useProjectParams();

    const { sendGpnAnalyticsEvent } = useSendGpnAnalyticsEvent();

    const { control, handleSubmit, reset, setError, clearErrors, setValue, formState } = useForm<
        FieldValuesBimWorkOrderChangeCreateOrEditStructure,
        AnyObject,
        TransformedValuesBimWorkOrderChangeCreateOrEditStructure
    >({
        criteriaMode: 'all',
    });

    const internalNumber = useWatch<FieldValuesBimWorkOrderChangeCreateOrEditStructure, 'internalNumber'>({
        control,
        name: 'internalNumber',
    });

    const status = useWatch<FieldValuesBimWorkOrderChangeCreateOrEditStructure, 'status'>({ control, name: 'status' });

    const isStatusApproved = status?.id === WorkOrderChangeStatusEnum.Approved;
    const isStatusApproving = status?.id === WorkOrderChangeStatusEnum.Approving;
    const isStatusFormation = status?.id === WorkOrderChangeStatusEnum.OnFormation;

    const currentTextDialogChangeStatus = useMemo(() => {
        if (isStatusApproving) {
            return (
                <div className={Styles.modalContent}>
                    <p className={Styles.modalText}>
                        После перевода в статус «На согласовании» дальнейшая работа по редактированию Изм. к НЗ будет
                        недоступна.
                    </p>
                    <span>Вы уверены, что хотите изменить статус?</span>
                </div>
            );
        }

        if (isStatusApproved) {
            return (
                <div className={Styles.modalContent}>
                    <p className={Styles.modalText}>
                        После перевода в статус «Согласовано» Изм. к НЗ станет текущей версией Наряд-Заказа.
                    </p>
                    <span>Вы уверены, что хотите изменить статус?</span>
                </div>
            );
        }

        if (isStatusFormation) {
            return (
                <div className={Styles.modalContent}>
                    <span>Вы уверены, что хотите изменить статус?</span>
                </div>
            );
        }

        return '';
    }, [isStatusApproving, isStatusApproved, isStatusFormation]);

    useEffect(() => {
        if (statusWorkOrderChange === WorkOrderChangeStatusEnum.Approving) {
            setIsConfirmationDialogChangeStatus(isStatusApproved || isStatusFormation);
        } else {
            setIsConfirmationDialogChangeStatus(isStatusApproving);
        }
    }, [isStatusApproved, isStatusApproving, isStatusFormation, statusWorkOrderChange]);

    const isInternalNumberUnique = useIsUniqueWorkOrderChangeInternalNumber({
        workOrderChangeId: Number(orderChangeId),
        workOrderId,
        newInternalNumber: internalNumber,
        isCreating,
    });

    const { bimWorkOrderChangeDefaultsData, bimWorkOrderChangeDefaultsStatus, resetBimWorkOrderChangeDefaultsData } =
        useWorkOrderChangeDefaults(workOrderId, isCreating);

    const { creatingData, creatingStatus, creatingError, creatingRequest, resetCreatingData, isCreatingInProgress } =
        useCreateWorkOrderChange();

    const { updatingData, updatingStatus, updatingError, updatingRequest, resetUpdatingData, isUpdatingInProgress } =
        useUpdateWorkOrderChange(onSuccess);

    // TODO: добавить расчет по договору
    const currentWorkOrder = useMemo(
        () => projectData?.contractNavigation?.data?.workOrderNavs?.find((workOrder) => workOrder.id === workOrderId),
        [projectData, workOrderId],
    );

    const isDisabledByWorkOrderStatus = currentWorkOrder?.status === WorkOrderStatusEnum.Delayed;
    const isContractStatusWork = contractStatus === ContractStatusEnum.InImplementation;

    const requestData = useMemo(
        () => ({
            data: creatingData || updatingData,
            error: creatingError || updatingError,
            status: creatingStatus || updatingStatus,
            result: creatingData?.result || updatingData?.result,
            validation: creatingData?.validation || updatingData?.validation,
        }),
        [creatingData, creatingError, creatingStatus, updatingData, updatingError, updatingStatus],
    );

    const resetAndCloseHandler = useCallback(() => {
        resetBimWorkOrderChangeDefaultsData(undefined);
        resetCreatingData(null);
        resetUpdatingData(null);
        onClose();
    }, [onClose, resetBimWorkOrderChangeDefaultsData, resetCreatingData, resetUpdatingData]);

    useEffect(() => {
        const formData = mapDataToForm({
            defaultsData: bimWorkOrderChangeDefaultsData?.bimWorkOrderChangeDefaults?.data,
            workOrderChangeData,
        });

        if (formData) {
            reset(formData);
        }
    }, [bimWorkOrderChangeDefaultsData, reset, workOrderChangeData]);

    useEffect(() => {
        if (internalNumber && workOrderChangeData?.internalNumber !== internalNumber && !isInternalNumberUnique) {
            setError('internalNumber', {
                message: 'Изм с таким номером уже существует. Введите другой номер',
            });
        } else {
            clearErrors('internalNumber');
        }
    }, [clearErrors, internalNumber, isInternalNumberUnique, setError, workOrderChangeData?.internalNumber]);

    useEffect(() => {
        if (isSubmitted && requestData.result) {
            const newId = requestData.data?.data?.id;

            if (isCreating && newId) {
                navigate(`/projects/${methodology}/${projectId}/orderChange/${newId}`);
            } else {
                refreshWorkOrderChangeData?.();
            }

            refreshProjectData();
            resetAndCloseHandler();
        }
    }, [
        isCreating,
        isSubmitted,
        methodology,
        navigate,
        projectId,
        refreshProjectData,
        refreshWorkOrderChangeData,
        requestData.data?.data?.id,
        requestData.result,
        resetAndCloseHandler,
    ]);

    const statusItems = useMemo(() => {
        const followers = (workOrderChangeData?.statusFollowers || []).map(
            (follower) => WorkOrderChangeStatusProps[follower],
        );

        return [
            ...followers,
            ...(bimWorkOrderChangeDefaultsData?.bimWorkOrderChangeDefaults?.data?.status
                ? [WorkOrderChangeStatusProps[bimWorkOrderChangeDefaultsData.bimWorkOrderChangeDefaults.data.status]]
                : []),
            ...(workOrderChangeData?.status ? [WorkOrderChangeStatusProps[workOrderChangeData.status]] : []),
        ];
    }, [
        bimWorkOrderChangeDefaultsData?.bimWorkOrderChangeDefaults?.data?.status,
        workOrderChangeData?.statusFollowers,
        workOrderChangeData?.status,
    ]);

    const isCommit = useMemo(
        () => isCreatingInProgress || isUpdatingInProgress,
        [isCreatingInProgress, isUpdatingInProgress],
    );

    const handleClose = useCallback(() => {
        if (!formState.isDirty && !isSubmitted) {
            resetAndCloseHandler();
        } else {
            setIsConfirmationDialogOpen(true);
        }
    }, [formState.isDirty, isSubmitted, resetAndCloseHandler]);

    const submitHandler = useCallback(
        (data: TransformedValuesBimWorkOrderChangeCreateOrEditStructure) => {
            const variables = {
                workOrderId,
                comment: data.comment,
                internalNumber: data.internalNumber,
                signingDate: data.signingDate,
            };

            if (isCreating) {
                dispatch(creatingRequest({ input: variables }));
            } else {
                dispatch(
                    updatingRequest({
                        input: {
                            id: Number(orderChangeId),
                            status: data.status.id as WorkOrderChangeStatusEnum,
                            ...variables,
                        },
                    }),
                );
            }

            setIsSubmitted(true);
        },
        [creatingRequest, dispatch, isCreating, orderChangeId, updatingRequest, workOrderId],
    );

    const handleSave = useCallback(() => {
        formRef.current?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
        refreshProjectData();
    }, [refreshProjectData]);

    const onSaveErrorReport = useCallback(() => {
        ExportCSVReport(requestData?.validation);
    }, [requestData?.validation]);

    const onCloseSaveErrorModal = useCallback(() => {
        resetAndCloseHandler();
        setIsSubmitted(false);
    }, [resetAndCloseHandler]);

    const onSubmitConfirmationDialog = useCallback(() => {
        resetAndCloseHandler();
    }, [resetAndCloseHandler]);

    const onCloseConfirmationDialog = useCallback(() => {
        setIsConfirmationDialogOpen(false);
    }, []);

    useEffect(() => {
        if (status?.id !== listSelectStatusValue[0]?.id) {
            setListSelectStatusValue([status, listSelectStatusValue[0]]);
        }
    }, [listSelectStatusValue, status, status?.id]);

    const onCloseConfirmationChangeStatus = useCallback(
        (isDeclined) => {
            if (!isDeclined) {
                if (statusWorkOrderChange && statusWorkOrderChange === WorkOrderChangeStatusEnum.OnFormation) {
                    setValue('status', { label: 'На формировании', id: 'ON_FORMATION' });
                }

                if (statusWorkOrderChange && statusWorkOrderChange === WorkOrderChangeStatusEnum.Approving) {
                    setValue('status', listSelectStatusValue[1]);
                }
            }
            setIsConfirmationDialogChangeStatus(false);
        },
        [listSelectStatusValue, setValue, statusWorkOrderChange],
    );

    const onSubmitConfirmationChangeStatus = useCallback(() => {
        sendGpnAnalyticsEvent({
            componentId: 'rim_change IZM button_pressed',
            projectId,
            contractId: projectData?.contractId,
            orderId: workOrderId,
            projectName: projectData?.name,
            projectType: projectData?.contractType,
            projectDO: projectData?.subsidiary?.name,
            methodology: projectData?.methodology,
            orderChangeStatus: status?.id as WorkOrderChangeStatusEnum,
        });

        setIsConfirmationDialogChangeStatus(false);
    }, [
        projectData?.contractId,
        projectData?.contractType,
        projectData?.methodology,
        projectData?.name,
        projectData?.subsidiary?.name,
        projectId,
        sendGpnAnalyticsEvent,
        status?.id,
        workOrderId,
    ]);

    const isNextStatusApproved = useMemo(() => status?.id === WorkOrderChangeStatusEnum.Approved, [status?.id]);

    const isDisabledActions =
        workOrderChangeData?.status === WorkOrderChangeStatusEnum.Approved ||
        isDisabledByWorkOrderStatus ||
        !isContractStatusWork;

    const seasonDistributionsBlock = useMemo(() => {
        if (bimWorkOrderChangeDefaultsData) {
            return bimWorkOrderChangeDefaultsData?.bimWorkOrderChangeDefaults?.data?.seasonDistributions
                .slice()
                .sort(({ year: yearA }, { year: yearB }) => yearA - yearB)
                .map(({ year }) => (
                    <GridItem key={`${year}`}>
                        <ControlledTextField
                            control={control}
                            label={`${year}`}
                            name={`season_${year}`}
                            type="text"
                            placeholder="%"
                            className={cnMixSpace({ mB: 's' })}
                            disabled
                        />
                    </GridItem>
                ));
        }

        if (workOrderChangeData) {
            return workOrderChangeData?.seasons
                .slice()
                .sort(({ workOrderSeason: workOrderSeasonA }, { workOrderSeason: workOrderSeasonB }) => {
                    const yearA = workOrderSeasonA?.year;
                    const yearB = workOrderSeasonB?.year;

                    if (yearA && yearB) {
                        return yearA - yearB;
                    }

                    return 0;
                })
                .map(({ workOrderSeason }) => (
                    <GridItem key={`${workOrderSeason?.year}`}>
                        <ControlledTextField
                            control={control}
                            label={`${workOrderSeason?.year}`}
                            name={`season_${workOrderSeason?.year}`}
                            type="text"
                            placeholder="%"
                            className={cnMixSpace({ mB: 's' })}
                            disabled
                        />
                    </GridItem>
                ));
        }

        return undefined;
    }, [bimWorkOrderChangeDefaultsData, control, workOrderChangeData]);

    return (
        <ModalDialog
            bodyMaxHeight="602px"
            btnConfirmDisabled={isDisabledActions}
            btnConfirmLabel={isCreating ? 'Создать' : 'Сохранить'}
            className={Styles.modal}
            isCommit={isCommit}
            isLoading={bimWorkOrderChangeDefaultsStatus === RequestStatus.FETCHING}
            isOpen
            onClose={handleClose}
            onConfirm={handleSave}
            title={`Данные по изменению к ${isFrameContract ? 'НЗ' : 'Расчету'}`}
            withTitleCloseButton
        >
            <form className={Styles.form} onSubmit={handleSubmit(submitHandler)} ref={formRef}>
                <Grid cols={1} className={Styles.grid}>
                    <GroupHeader mB="s">Основная информация</GroupHeader>
                    <GridItem>
                        <ControlledSelectField
                            control={control}
                            label="Статус"
                            name="status"
                            items={statusItems}
                            className={cnMixSpace({ mB: 's' })}
                            disabled={isCreating || isDisabledActions}
                        />
                    </GridItem>
                    <GridItem>
                        <ControlledTextField
                            control={control}
                            label="Предмет"
                            name="subject"
                            placeholder="Предмет"
                            className={cnMixSpace({ mB: 's' })}
                            disabled
                        />
                    </GridItem>
                    <GridItem>
                        <ControlNumberField
                            control={control}
                            label="Номер Изм. к НЗ"
                            name="internalNumber"
                            placeholder="Номер"
                            type="number"
                            labelPosition="left"
                            width="full"
                            size="s"
                            className={cnMixSpace({ mB: 's' })}
                            decimalScale={0}
                            thousandSeparator={false}
                            min={0}
                            disabled={
                                isDisabledActions || workOrderChangeData?.status === WorkOrderChangeStatusEnum.Approving
                            }
                            rules={{
                                ...requiredBaseRule,
                                min: {
                                    value: 0,
                                    message: 'недопустимы отрицательные значения',
                                },
                            }}
                            required
                        />
                    </GridItem>
                    <GridItem>
                        <ControlledTextField
                            control={control}
                            label="Автор"
                            name="authoredBy"
                            className={cnMixSpace({ mB: 's' })}
                            disabled
                        />
                    </GridItem>
                    <GridItem>
                        <ControlledTextField
                            control={control}
                            label="Дата изменения"
                            name="updatedAt"
                            className={cnMixSpace({ mB: 's' })}
                            disabled
                        />
                    </GridItem>
                    <GridItem>
                        <ControlledDateField
                            control={control}
                            label="Дата подписания"
                            name="signingDate"
                            type="date"
                            className={cnMixSpace({ mB: 'l' })}
                            disabled={isDisabledActions}
                            rules={isNextStatusApproved ? requiredContractChangeApprovedRule : undefined}
                            required={isNextStatusApproved}
                        />
                    </GridItem>
                    <GridItem>
                        <ControlledTextField
                            control={control}
                            label="Комментарий"
                            name="comment"
                            placeholder="Комментарий"
                            className={cnMixSpace({ mB: 's' })}
                            rules={{
                                maxLength: {
                                    value: 512,
                                    message: 'не более 512 символов',
                                },
                            }}
                            disabled={isDisabledActions}
                        />
                    </GridItem>
                </Grid>
                <Grid cols={1} className={Styles.grid}>
                    <GroupHeader mB="s">% перераспределения Итогов</GroupHeader>
                    {seasonDistributionsBlock}
                </Grid>
            </form>
            <ErrorHandlingModal
                titleText={`Данные по изменению к ${isFrameContract ? 'нз' : 'расчету'}`}
                isSubmitted={isSubmitted}
                statusRequest={requestData.status}
                dataRequest={requestData.data}
                errorsRequest={requestData.error}
                handleCloseModalError={onCloseSaveErrorModal}
                handleCreateReport={onSaveErrorReport}
            />
            <ConfirmationDialog
                textHeader="Отмена действия"
                textConfirmation="Вы уверены, что хотите отменить? Заполненные данные не будут сохранены"
                cancelButtonText="Не отменять"
                confirmButtonText="Отменить"
                isOpened={isConfirmationDialogOpen}
                onSubmit={onSubmitConfirmationDialog}
                onClose={onCloseConfirmationDialog}
            />
            <ConfirmationDialog
                textHeader="Изменение статуса"
                textConfirmation={currentTextDialogChangeStatus}
                cancelButtonText="Не изменять"
                confirmButtonText="Изменить"
                isOpened={isConfirmationDialogChangeStatus}
                onSubmit={onSubmitConfirmationChangeStatus}
                onClose={onCloseConfirmationChangeStatus}
            />
        </ModalDialog>
    );
};

BimWorkOrderChangeCreateOrEdit.displayName = 'BimWorkOrderChangeCreateOrEdit';
